import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import styles from './TermsConditionsPage.module.css'

function Section({ num, title, children }) {
  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>
        {num ? (
          <span className={styles.sectionNum}>{num}</span>
        ) : (
          <span className={styles.sectionBar} aria-hidden="true" />
        )}
        {title}
      </h2>
      <div className={`${styles.sectionBody} ${num ? styles.sectionBodyIndented : ''}`}>
        {children}
      </div>
    </div>
  )
}

function Brand({ children }) {
  return <strong className={styles.brand}>{children}</strong>
}

function Highlight({ children }) {
  return <span className={styles.highlight}>{children}</span>
}

export default function TermsConditionsPage() {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <div className={styles.page}>
      {/* ── Page header ── */}
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.back}>
            <span className={styles.backArrow}>←</span> Back to Home
          </Link>
          <div className={styles.headerTitle}>
            <div className={styles.iconWrap}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </div>
            <div>
              <h1 className={styles.h1}>Terms &amp; Conditions</h1>
              <p className={styles.subtitle}>NexusAIWeb &amp; TechIntelliVerse Private Limited · Last updated: May 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className={styles.body}>
        <div className={styles.card}>

          {/* Brand strip */}
          <div className={styles.pillStrip}>
            {[
              { name: 'NexusAIWeb',  cls: styles.pillOrange },
              { name: 'PromptNexus', cls: styles.pillIndigo },
              { name: 'InternNexus', cls: styles.pillCyan },
              { name: 'SkillNexus',  cls: styles.pillIndigo },
              { name: 'MentorNexus', cls: styles.pillGold },
            ].map(b => (
              <span key={b.name} className={`${styles.pill} ${b.cls}`}>{b.name}</span>
            ))}
            <span className={`${styles.pill} ${styles.pillMuted}`}>
              Brand names of TechIntelliVerse Pvt. Ltd.
            </span>
          </div>

          <Section title="Overview">
            <p>
              Welcome to the platforms of <Brand>TechIntelliVerse Private Limited</Brand> — the legal entity
              behind all NexusAIWeb ecosystem products. The brand names <Brand>NexusAIWeb</Brand>,{' '}
              <Brand>PromptNexus</Brand>, <Brand>InternNexus</Brand>, <Brand>SkillNexus</Brand>, and{' '}
              <Brand>MentorNexus</Brand> are owned by TechIntelliVerse Private Limited and operated under a
              unified technology platform.
            </p>
            <p>
              These Terms &amp; Conditions govern your access to and use of any TechIntelliVerse platform,
              including registering for an account, participating in challenges, enrolling in programs, or
              interacting with any content on these platforms.
            </p>
            <p>
              By accessing or using these platforms, you agree to be bound by these Terms. If you do not
              agree, you must not use any TechIntelliVerse platform.
            </p>
            <div className={styles.alertBox}>
              <p>These terms constitute a legally binding agreement between you and TechIntelliVerse Private Limited.</p>
            </div>
          </Section>

          <Section num="1" title="Acceptance of Terms">
            <p>By creating an account or using any part of a NexusAIWeb platform, you confirm that you:</p>
            <ul>
              <li>Are at least 13 years of age</li>
              <li>Have read and understood these Terms &amp; Conditions</li>
              <li>Have read and understood our Privacy Policy</li>
              <li>Agree to be legally bound by these terms</li>
              <li>Have the legal capacity to enter into a binding agreement</li>
            </ul>
            <p>If you are under 18, you confirm that a parent or legal guardian has reviewed and consented to your use of these platforms.</p>
          </Section>

          <Section num="2" title="Account Registration">
            <p>A single TechIntelliVerse account grants you access to all enabled NexusAIWeb ecosystem platforms. You agree to:</p>
            <ul>
              <li>Provide accurate, complete, and current registration information</li>
              <li>Maintain the security of your password and account credentials</li>
              <li>Notify us immediately of any unauthorised use of your account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Not share your account credentials with any other person</li>
              <li>Not create multiple accounts for the same individual</li>
            </ul>
            <p>TechIntelliVerse Private Limited reserves the right to suspend or terminate accounts that violate these terms, without prior notice.</p>
          </Section>

          <Section num="3" title="Platform Use & Acceptable Conduct">
            <p>You agree to use NexusAIWeb and all TechIntelliVerse platforms only for lawful purposes and in a manner that does not:</p>
            <ul>
              <li>Violate any applicable local, national, or international law or regulation</li>
              <li>Infringe the intellectual property rights of TechIntelliVerse or any third party</li>
              <li>Transmit unsolicited or unauthorised advertising or promotional material</li>
              <li>Attempt to gain unauthorised access to any part of any platform</li>
              <li>Interfere with or disrupt the integrity or performance of any platform</li>
              <li>Submit false, misleading, or fraudulent information</li>
              <li>Engage in cheating, plagiarism, or any unfair practice in PromptNexus challenges</li>
              <li>Use automated tools, bots, or scripts to interact with any platform</li>
              <li>Reverse engineer, decompile, or disassemble any part of any platform</li>
            </ul>
          </Section>

          <Section num="4" title="Challenge Participation & Fair Play (PromptNexus)">
            <p>By participating in any PromptNexus challenge, you acknowledge and agree to the following fair play rules:</p>
            <ul>
              <li>All prompt submissions must be your own original work</li>
              <li>Use of AI tools to auto-generate complete prompt solutions may result in immediate disqualification</li>
              <li>Sharing challenge problem statements or solution strategies with others during an active challenge is strictly prohibited</li>
              <li>Results and rankings are determined by <Brand>NexusEval</Brand>, TechIntelliVerse's proprietary AI evaluation system, and are final</li>
              <li>TechIntelliVerse reserves the right to disqualify any participant found violating these fair play rules</li>
            </ul>
            <p>Challenge registration fees, where applicable, are non-refundable once the challenge has started, except in cases where the challenge is cancelled by TechIntelliVerse.</p>
          </Section>

          <Section num="5" title="Certificates & Credentials">
            <p>Digital certificates issued via PromptNexus are awarded based on challenge performance as evaluated by NexusEval. You agree that:</p>
            <ul>
              <li>Certificates are issued by <Brand>TechIntelliVerse Private Limited</Brand> under the PromptNexus brand and represent your performance on a specific challenge</li>
              <li>You must represent your certificates accurately and not misstate their level, scope, or issuing entity</li>
              <li>TechIntelliVerse reserves the right to revoke any certificate issued in error or obtained through dishonest means</li>
              <li>Certificates include a unique verification code and public QR code that may be verified at any time via the PromptNexus verification page</li>
            </ul>
          </Section>

          <Section num="6" title="Payments & Refund Policy">
            <p>Some features on NexusAIWeb platforms require payment. All payments are processed via <Brand>Razorpay</Brand>, an RBI-regulated payment gateway. By making a payment, you agree that:</p>
            <ul>
              <li>All prices are displayed in Indian Rupees (INR) inclusive of applicable taxes</li>
              <li>You authorise TechIntelliVerse to charge your selected payment method for the stated amount</li>
              <li>TechIntelliVerse does not store your card or banking details on our servers</li>
              <li>Payments are subject to Razorpay's own terms of service</li>
            </ul>
            <p><strong>Challenge Fees:</strong> Non-refundable once a challenge is underway. Full refund issued if TechIntelliVerse cancels the challenge.</p>
            <p><strong>PromptPath Programs:</strong> Refundable within 24 hours of purchase if less than 10% of program content has been accessed. Submit requests with your order ID to <Highlight>support@nexusaiweb.com</Highlight>. Refunds processed within 5–7 business days.</p>
          </Section>

          <Section num="7" title="Intellectual Property">
            <p>
              All content on NexusAIWeb platforms — including challenge problem statements, learning materials, UI
              design, logos, and the brand names <Brand>NexusAIWeb</Brand>, <Brand>PromptNexus</Brand>,{' '}
              <Brand>InternNexus</Brand>, <Brand>SkillNexus</Brand>, <Brand>MentorNexus</Brand>, and the{' '}
              <Brand>NexusEval</Brand> evaluation framework — are the intellectual property of{' '}
              <Brand>TechIntelliVerse Private Limited</Brand> and are protected under applicable copyright,
              trademark, and IP laws.
            </p>
            <p>You may not reproduce, distribute, sublicense, or create derivative works from our content without explicit prior written permission from TechIntelliVerse Private Limited.</p>
            <p>You retain ownership of the prompt solutions you submit to PromptNexus. By submitting, you grant TechIntelliVerse a non-exclusive, royalty-free, worldwide licence to use anonymised submission data for AI model improvement, platform analytics, and research purposes.</p>
          </Section>

          <Section num="8" title="Privacy & Cookies">
            <p>Your use of NexusAIWeb platforms is governed by our <Link to="/privacy" className={styles.inlineLink}>Privacy Policy</Link>, published by TechIntelliVerse Private Limited and incorporated into these Terms by reference. Our Privacy Policy explains:</p>
            <ul>
              <li>What personal information we collect and why</li>
              <li>How we use cookies and tracking technologies (including Google Analytics)</li>
              <li>How payments are processed and what data is shared with Razorpay</li>
              <li>Your rights regarding your personal data</li>
            </ul>
            <p>By using any NexusAIWeb platform, you consent to the use of cookies as described in our Privacy Policy.</p>
          </Section>

          <Section num="9" title="Third-Party Links & Services">
            <p>NexusAIWeb platforms may contain links to third-party websites, services, or resources. These are provided for convenience only. TechIntelliVerse has no control over and accepts no responsibility for the content, privacy policies, or practices of any third-party site.</p>
            <p>We encourage you to review the terms and privacy policies of any third-party services you interact with through our platforms, including LinkedIn, Google, Razorpay, and Azure.</p>
          </Section>

          <Section num="10" title="Disclaimers & Limitation of Liability">
            <p>NexusAIWeb platforms are provided on an "as is" and "as available" basis. To the fullest extent permitted by Indian law, TechIntelliVerse Private Limited disclaims all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.</p>
            <p>TechIntelliVerse shall not be liable for:</p>
            <ul>
              <li>Any indirect, incidental, special, or consequential damages</li>
              <li>Loss of data, profits, or business opportunities</li>
              <li>Platform downtime or service interruptions</li>
              <li>Errors or inaccuracies in NexusEval AI evaluation results</li>
              <li>Third-party service failures (including Razorpay, Azure, Google)</li>
            </ul>
            <p>TechIntelliVerse's total liability for any claims shall not exceed the amount you paid to us in the 3 months preceding the claim.</p>
          </Section>

          <Section num="11" title="Termination">
            <p>TechIntelliVerse reserves the right to suspend or terminate your access to any or all NexusAIWeb platforms at any time, with or without notice, for conduct that violates these Terms or is otherwise objectionable at our sole discretion.</p>
            <p>You may request account deletion at any time by contacting <Highlight>support@nexusaiweb.com</Highlight>. Upon termination, your right to use all TechIntelliVerse platforms ceases immediately.</p>
          </Section>

          <Section num="12" title="Changes to These Terms">
            <p>TechIntelliVerse reserves the right to modify these Terms &amp; Conditions at any time. Material changes will be communicated by posting the updated terms on this page with a revised effective date and, where appropriate, by email notification.</p>
            <p>Your continued use of any NexusAIWeb platform after changes are posted constitutes acceptance of the revised Terms.</p>
          </Section>

          <Section num="13" title="Governing Law & Dispute Resolution">
            <p>These Terms shall be governed by and construed in accordance with the laws of the Republic of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts in India.</p>
            <p>We encourage you to contact us first to resolve disputes amicably by writing to <Highlight>legal@nexusaiweb.com</Highlight> before initiating any formal legal proceedings.</p>
          </Section>

          <Section num="14" title="Contact">
            <p>For any questions or concerns regarding these Terms, please contact:</p>
            <div className={styles.contactBox}>
              <p className={styles.contactName}>TechIntelliVerse Private Limited</p>
              <p className={styles.contactLine}>Brands: NexusAIWeb · PromptNexus · InternNexus · SkillNexus · MentorNexus</p>
              <p className={styles.contactLine}>Legal: legal@nexusaiweb.com</p>
              <p className={styles.contactLine}>Support: support@nexusaiweb.com</p>
              <p className={styles.contactLine}>Website: nexusaiweb.com</p>
            </div>
          </Section>

        </div>

        {/* Page footer strip */}
        <div className={styles.pageFooter}>
          <span className={styles.pageFooterBrand}>NexusAIWeb</span>
          <span className={styles.pageFooterDot}>·</span>
          <span className={styles.pageFooterText}>TechIntelliVerse Private Limited</span>
          <span className={styles.pageFooterDot}>·</span>
          <Link to="/privacy" className={styles.pageFooterLink}>Privacy Policy</Link>
          <span className={styles.pageFooterDot}>·</span>
          <Link to="/" className={styles.pageFooterLink}>Home</Link>
        </div>
      </div>
    </div>
  )
}
